import React, { useEffect, useState } from 'react';
import { brandApi, uploadApi } from '../services/api';
import { Plus, Pencil, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_BRAND = { name: '', description: '', imageUrl: '', order: 0, isActive: true };

export default function AdminBrands() {
  const [brands,   setBrands]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null); // null | { mode:'add'|'edit', data }
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await brandApi.getAll();
    setBrands(res.data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.upload(file, 'brands');
      setModal((m) => ({ ...m, data: { ...m.data, imageUrl: res.data.url } }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const { mode, data } = modal;
    try {
      if (mode === 'add') {
        await brandApi.create(data);
        toast.success('Brand created');
      } else {
        await brandApi.update(data.id, data);
        toast.success('Brand updated');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving brand');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    await brandApi.delete(id);
    toast.success('Brand deleted');
    load();
  };

  const toggleActive = async (brand) => {
    await brandApi.update(brand.id, { isActive: !brand.isActive });
    load();
  };

  const setField = (field, value) => setModal((m) => ({ ...m, data: { ...m.data, [field]: value } }));

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Our Brands</h1>
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'add', data: { ...EMPTY_BRAND, order: brands.length } })}>
          <Plus size={18} /> Add Brand
        </button>
      </div>

      <p className="meta-text" style={{ marginBottom: 16 }}>
        Controls the brand cards shown on the storefront's "Our Brands" page.
      </p>

      {loading ? <div className="spinner" /> : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order</th><th>Logo</th><th>Name</th><th>Active</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b.id}>
                  <td>{b.order}</td>
                  <td>{b.imageUrl && <img src={b.imageUrl} alt={b.name} className="row-thumbnail" />}</td>
                  <td>{b.name}</td>
                  <td>
                    <button className={`btn btn-sm ${b.isActive ? 'btn-primary' : 'btn-outline'}`} onClick={() => toggleActive(b)}>
                      {b.isActive ? 'Active' : 'Hidden'}
                    </button>
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => setModal({ mode: 'edit', data: { ...b } })}>
                      <Pencil size={16} />
                    </button>
                    <button className="icon-btn danger" onClick={() => remove(b.id)}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {brands.length === 0 && (
                <tr><td colSpan={5} className="empty-row">No brands yet — add one to populate the Our Brands page.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modal.mode === 'add' ? 'Add Brand' : 'Edit Brand'}</h2>

            <div className="form-field">
              <label>Name *</label>
              <input value={modal.data.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Prabhar Premixes" />
            </div>
            <div className="form-field">
              <label>Description</label>
              <textarea value={modal.data.description} onChange={(e) => setField('description', e.target.value)}
                placeholder="Short description shown under the brand name" rows={3} />
            </div>
            <div className="form-field">
              <label>Order</label>
              <input type="number" value={modal.data.order} onChange={(e) => setField('order', parseInt(e.target.value) || 0)} />
            </div>
            <div className="form-field">
              <label>Logo / Image</label>
              <div className="image-upload-row">
                {modal.data.imageUrl && <img src={modal.data.imageUrl} alt="preview" className="upload-preview" />}
                <label className="upload-label">
                  <Upload size={18}/> {uploading ? 'Uploading…' : 'Upload Image'}
                  <input type="file" accept="image/*" hidden onChange={handleUpload} />
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={uploading}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
